import { http } from '@/api/http';
import type { OperationResult, Schemas } from '@/api/shared';

/**
 * Live PhenoTips POST/PUT body for `/persons/{guid}/features[/{id}]`.
 *
 * Drifts from the checked-in OpenAPI `FeatureInput` (which uses
 * `hpo_id` / `status: present|absent|unknown` / flat `onset` / `severity` /
 * `notes`). The live `FeatureDTO` is HPO-tree-aware: required `type` +
 * `isStandard` + `label`, three-state `observed: 'Y' | 'N' | 'U'`, and
 * structured qualifier groups (severity, age-of-onset, notes, etc).
 *
 * `type` is almost always `'PHENOTYPE'` for clinician-entered HPO terms;
 * the other enum members are reserved for prenatal phenotypes, modes of
 * inheritance, and the OMIM-side records (still served by this endpoint
 * family in enterprise).
 */
export type LiveFeatureType =
  | 'PRENATAL_PHENOTYPE'
  | 'MODE_OF_INHERITANCE'
  | 'PHENOTYPE'
  | 'CANCER'
  | 'DISORDER'
  | 'CLINICAL_DIAGNOSIS'
  | 'AGE_OF_ONSET';

export type LiveFeatureObserved = 'Y' | 'N' | 'U';

export type LiveFeatureQualifierType =
  | 'AGE_OF_ONSET'
  | 'LATERALITY'
  | 'PACE_OF_PROGRESSION'
  | 'SEVERITY'
  | 'SPATIAL_PATTERN'
  | 'TEMPORAL_PATTERN'
  | 'PRIMARY'
  | 'NOTES'
  | 'AGE_AT_DIAGNOSIS'
  /**
   * Phase 1 spec — Phenotypes. Records whether a feature was observed
   * pre-appointment, post-appointment, or both. Stored as a
   * `stringValue` carrying the enum name (`PRE_APPOINTMENT` / `POST_APPOINTMENT`
   * / `BOTH`) so legacy readers can still surface it. See PTV2-1051 #3.
   */
  | 'OCCURRENCE';

/**
 * The BE deserialises `FeatureQualifierDTO` with Jackson-strict typing —
 * `termId` / `guid` / `label` / `stringValue` are non-nullable strings on
 * the wire, even though the OpenAPI snapshot lists only `type` as
 * required. Senders supply empty strings for fields they don't have
 * (e.g. free-text qualifiers leave `termId` and `guid` blank).
 */
export interface LiveFeatureQualifier {
  readonly type: LiveFeatureQualifierType;
  readonly termId: string;
  readonly guid: string;
  readonly label: string;
  readonly stringValue: string;
}

export interface LiveFeatureQualifierGroup {
  /** Server-assigned for new groups; safe to omit on POST. */
  readonly id?: string;
  readonly featureQualifiers: LiveFeatureQualifier[];
}

export interface LiveFeatureDate {
  readonly day: number;
  readonly month: number;
  readonly year: number;
}

export interface LiveFeatureSource {
  readonly type: string;
  readonly label: string;
}

/**
 * Payload for POST/PUT `/persons/{guid}/features[/{id}]`.
 *
 * Even though the OpenAPI snapshot only requires `type` / `isStandard` /
 * `label`, the live BE rejects the create when sibling arrays
 * (`childrenIds`, `supportingDocuments`, `featureQualifierGroups`) are
 * omitted — Jackson strict-deserialises the DTO. We always include them
 * with safe defaults. `parentId` is nullable on the wire despite the
 * Java interface declaring it `string`.
 */
export interface LiveFeatureCreateInput {
  readonly type: LiveFeatureType;
  readonly isStandard: boolean;
  readonly label: string;
  readonly termId: string;
  readonly observed: LiveFeatureObserved;
  readonly parentId: string | null;
  readonly childrenIds: string[];
  readonly supportingDocuments: unknown[];
  readonly dateObserved: LiveFeatureDate | null;
  readonly featureQualifierGroups: LiveFeatureQualifierGroup[];
  readonly source?: LiveFeatureSource;
}

export const featuresApi = {
  /** GET /persons/{guid}/features — HPO phenotypic features for a patient. */
  getFeatures: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getFeatures'>> =>
    http.get<OperationResult<'getFeatures'>>(
      `/persons/${encodeURIComponent(guid)}/features`,
      options,
    ),

  /**
   * POST /persons/{guid}/features — add a phenotypic feature.
   *
   * Body speaks the live `FeatureDTO` shape (camelCase, three-state
   * `observed`, partial `dateObserved`, structured qualifier groups). The
   * list GET on the same path already returns this shape and the read
   * mapper handles both — see `mapFeature`.
   */
  addFeature: (
    guid: string,
    body: LiveFeatureCreateInput,
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Feature']> =>
    http.post<Schemas['Feature']>(
      `/persons/${encodeURIComponent(guid)}/features`,
      body,
      options,
    ),

  /**
   * PUT /persons/{personGuid}/features/{id} — replace a feature.
   * Body uses the live `FeatureDTO` shape (same as POST).
   */
  updateFeature: (
    personGuid: string,
    id: string,
    body: LiveFeatureCreateInput,
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Feature']> =>
    http.put<Schemas['Feature']>(
      `/persons/${encodeURIComponent(personGuid)}/features/${encodeURIComponent(id)}`,
      body,
      options,
    ),

  /** DELETE /persons/{personGuid}/features/{id} — remove a feature. */
  deleteFeature: (
    personGuid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(personGuid)}/features/${encodeURIComponent(id)}`,
      options,
    ),
};
